// useClampText.ts
import {useEffect, RefObject, useRef} from 'react';
import {INFO_CONTENT_ABSOLUTE_MIN_LINES} from 'components/ProductSnippetNew/ProductSnippet.constants';

type TUseClampText = {
    rootRef: RefObject<HTMLDivElement>;
    titleRef: RefObject<HTMLDivElement>;
    descriptionRef: RefObject<HTMLDivElement>;
    pixelOverflow: number;
    desc: string;
};

const clampElement = (element: HTMLElement, lines: number, lineHeight: number) => {
    element.style.height = `${lines * lineHeight}px`;
    element.style.webkitLineClamp = String(lines);
    element.style.display = '-webkit-box';
    element.style.webkitBoxOrient = 'vertical';
    element.style.overflow = 'hidden';
};

const resetStyles = (element: HTMLElement) => {
    element.style.height = '';
    element.style.webkitLineClamp = '';
    element.style.display = '';
    element.style.webkitBoxOrient = '';
    element.style.overflow = '';
};

export const useClampText = (params: TUseClampText) => {
    const {rootRef, titleRef, descriptionRef, pixelOverflow, desc} = params;

    // Кэшируем результаты для предотвращения лишних пересчетов
    const lastResultRef = useRef<{titleLines: number; descLines: number; desc: string; pixelOverflow: number} | null>(
        null,
    );
    const rafIdRef = useRef<number>();

    useEffect(() => {
        const rootEl = rootRef.current;
        const titleEl = titleRef.current;
        const descEl = descriptionRef.current;

        if (!rootEl || !titleEl || !descEl) {
            return () => {};
        }

        // Проверяем, нужно ли пересчитывать
        if (lastResultRef.current?.desc === desc && lastResultRef.current?.pixelOverflow === pixelOverflow) {
            // Применяем кэшированный результат
            const {titleLines, descLines} = lastResultRef.current;
            const titleLineHeight = parseInt(getComputedStyle(titleEl).lineHeight, 10);
            const descLineHeight = parseInt(getComputedStyle(descEl).lineHeight, 10);

            if (titleLines < Math.ceil(titleEl.scrollHeight / titleLineHeight)) {
                clampElement(titleEl, titleLines, titleLineHeight);
            }
            if (descLines < Math.ceil(descEl.scrollHeight / descLineHeight)) {
                clampElement(descEl, descLines, descLineHeight);
            }
            return () => {};
        }

        // Отменяем предыдущий RAF
        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
        }

        // Запускаем вычисления в следующем кадре
        rafIdRef.current = requestAnimationFrame(() => {
            // Сбрасываем стили
            resetStyles(titleEl);
            resetStyles(descEl);

            // Получаем параметры один раз
            const titleStyles = getComputedStyle(titleEl);
            const descStyles = getComputedStyle(descEl);

            const titleLineHeight = parseInt(titleStyles.lineHeight, 10);
            const descLineHeight = parseInt(descStyles.lineHeight, 10);
            const titleScrollHeight = titleEl.scrollHeight;
            const descScrollHeight = descEl.scrollHeight;

            const maxTitleLines = Math.ceil(titleScrollHeight / titleLineHeight);
            const maxDescLines = Math.ceil(descScrollHeight / descLineHeight);

            const containerHeight = rootEl.offsetHeight;
            const availableHeight = containerHeight - pixelOverflow;

            // Минимальные значения
            const minTitleLines = INFO_CONTENT_ABSOLUTE_MIN_LINES;
            const minDescLines = INFO_CONTENT_ABSOLUTE_MIN_LINES;

            // Функция проверки
            const checkFit = (titleLinesNum: number, descLinesNum: number): boolean => {
                // Применяем стили
                if (titleLinesNum < maxTitleLines) {
                    titleEl.style.height = `${titleLinesNum * titleLineHeight}px`;
                    titleEl.style.webkitLineClamp = String(titleLinesNum);
                    titleEl.style.display = '-webkit-box';
                    titleEl.style.webkitBoxOrient = 'vertical';
                    titleEl.style.overflow = 'hidden';
                } else {
                    titleEl.style.height = '';
                    titleEl.style.webkitLineClamp = '';
                    titleEl.style.display = '';
                    titleEl.style.webkitBoxOrient = '';
                    titleEl.style.overflow = '';
                }

                if (descLinesNum < maxDescLines) {
                    descEl.style.height = `${descLinesNum * descLineHeight}px`;
                    descEl.style.webkitLineClamp = String(descLinesNum);
                    descEl.style.display = '-webkit-box';
                    descEl.style.webkitBoxOrient = 'vertical';
                    descEl.style.overflow = 'hidden';
                } else {
                    descEl.style.height = '';
                    descEl.style.webkitLineClamp = '';
                    descEl.style.display = '';
                    descEl.style.webkitBoxOrient = '';
                    descEl.style.overflow = '';
                }

                // Принудительный reflow
                // eslint-disable-next-line no-void
                void titleEl.offsetHeight;

                const titleHeight = titleEl.getBoundingClientRect().height;
                const descHeight = descEl.getBoundingClientRect().height;

                return titleHeight + descHeight <= availableHeight + 0.5;
            };

            // Поиск оптимальных строк
            let titleLines = maxTitleLines;
            let descLines = maxDescLines;

            // Пробуем максимальные значения
            if (!checkFit(maxTitleLines, maxDescLines)) {
                let found = false;

                // Пробуем уменьшать заголовок
                for (let t = maxTitleLines - 1; t >= minTitleLines; t--) {
                    if (checkFit(t, maxDescLines)) {
                        titleLines = t;
                        descLines = maxDescLines;
                        found = true;
                        break;
                    }
                }

                // Если не помогло, уменьшаем описание
                if (!found) {
                    for (let d = maxDescLines - 1; d >= minDescLines; d--) {
                        if (checkFit(minTitleLines, d)) {
                            titleLines = minTitleLines;
                            descLines = d;
                            found = true;
                            break;
                        }
                    }
                }

                // Крайний случай
                if (!found) {
                    titleLines = minTitleLines;
                    descLines = minDescLines;
                    checkFit(minTitleLines, minDescLines);
                }
            }

            // Кэшируем результат
            lastResultRef.current = {
                titleLines,
                descLines,
                desc,
                pixelOverflow,
            };

            // Применяем финальные стили (уже применены в последнем checkFit)
            if (titleLines >= maxTitleLines) {
                resetStyles(titleEl);
            }
            if (descLines >= maxDescLines) {
                resetStyles(descEl);
            }

            rafIdRef.current = undefined;
        });

        return () => {
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
            }
        };
    }, [pixelOverflow, rootRef, titleRef, descriptionRef, desc]);
};
